import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import type {
  ParsedQueryIntent,
  MatchScoreBreakdown,
} from '@/services/marketplaceSearchService';
import type { SearchContextSnapshot } from '@/types/searchResults';

export type OfferingType = 'ONE_TO_ONE' | 'GROUP' | 'CRASH_COURSE';

export interface OfferingOneToOneBlock {
  sessionDurationMinutes: number | null;
}
export interface OfferingGroupBlock {
  batchSizeMax: number | null;
  sessionsPerWeek: number | null;
  sessionDurationMinutes: number | null;
  nextBatchStartDate: string | null;
}
export interface OfferingCrashCourseBlock {
  courseStartDate: string | null;
  courseEndDate: string | null;
  sessionDurationMinutes: number | null;
  mockTestsIncluded: number | null;
  totalSessions: number | null;
}

/** Offering-centric search result — one card per published offering (ADR-088). */
export interface OfferingSearchResult {
  offeringId: string;
  offeringType: OfferingType;
  title: string;
  subjectId: string | null;
  subjectName: string | null;
  teacherHandle: string | null;
  teacherName: string | null;
  teacherProfileImageUrl: string | null;
  /** Teacher's active subscription tier; null when the teacher has no active subscription */
  teacherTier: 'SILVER' | 'GOLD' | 'PLATINUM' | null;
  teachingMode: string | null;
  gradeBands: string[];
  /** per-session price in the resolved currency */
  minPrice: number | null;
  maxPrice: number | null;
  /** per-session early-bird price; below minPrice when a discount applies, else null */
  minEarlyBirdPrice: number | null;
  currency: string | null;
  trialSessionAvailable: boolean;
  distanceKm: number | null;
  /** 0–100 for authenticated students with a saved goal profile; null otherwise */
  matchScore: number | null;
  matchBand: string | null;
  /** Aggregate rating (cluster 3): NULL until the offering's teacher has enough PUBLISHED reviews. */
  ratingAvg: number | null;
  /** Count of PUBLISHED reviews contributing to `ratingAvg`. */
  ratingCount: number;
  oneToOne?: OfferingOneToOneBlock | null;
  group?: OfferingGroupBlock | null;
  crashCourse?: OfferingCrashCourseBlock | null;
}

export interface OfferingSearchPageResponse {
  content: OfferingSearchResult[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  parsedIntent?: ParsedQueryIntent | null;
  fallbackMode: boolean;
  closestFits: OfferingSearchResult[] | null;
  resolvedSubjectId?: string | null;
}

/** Filters common to all three per-type endpoints. */
export interface CommonOfferingSearchParams {
  q?: string;
  page?: number;
  size?: number;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  subjectId?: string;
  gradeBand?: string[];
  language?: string[];
  curriculum?: string[];
  teachingMode?: string[];
  availabilityTimeOfDay?: string;
  trialSessionAvailable?: boolean;
  targetExamId?: string;
  topicKeywords?: string;
  /** Topic UUIDs — narrows to offerings tagged with ANY selected topic */
  topicIds?: string[];
  priceMin?: number;
  priceMax?: number;
  /** RELEVANCE (default) | PRICE_LOW | PRICE_HIGH | POPULARITY */
  sortBy?: string;
  accessToken?: string;
}

export interface GroupOfferingSearchParams extends CommonOfferingSearchParams {
  batchSizeMax?: number;
  sessionsPerWeek?: number;
  sessionDurationMinutes?: number;
  /** Eligibility window on next batch start date — carousels/dashboard */
  startsWithinDays?: number;
}

export interface CrashCourseSearchParams extends CommonOfferingSearchParams {
  sessionDurationMinutes?: number;
  mockTestsIncluded?: boolean;
  courseStartDate?: string;
  courseEndDate?: string;
  startsWithinDays?: number;
}

function buildCommon(sp: URLSearchParams, p: CommonOfferingSearchParams): void {
  if (p.q) sp.set('q', p.q);
  if (p.latitude != null) sp.set('latitude', String(p.latitude));
  if (p.longitude != null) sp.set('longitude', String(p.longitude));
  if (p.radiusKm != null) sp.set('radiusKm', String(p.radiusKm));
  if (p.subjectId) sp.set('subjectId', p.subjectId);
  p.gradeBand?.forEach(v => sp.append('gradeBand', v));
  p.language?.forEach(v => sp.append('language', v));
  p.curriculum?.forEach(v => sp.append('curriculum', v));
  p.teachingMode?.forEach(v => sp.append('teachingMode', v));
  if (p.availabilityTimeOfDay) sp.set('availabilityTimeOfDay', p.availabilityTimeOfDay);
  if (p.trialSessionAvailable != null) sp.set('trialSessionAvailable', String(p.trialSessionAvailable));
  if (p.targetExamId) sp.set('targetExamId', p.targetExamId);
  if (p.topicKeywords) sp.set('topicKeywords', p.topicKeywords);
  p.topicIds?.forEach(v => sp.append('topicIds', v));
  if (p.priceMin != null) sp.set('priceMin', String(p.priceMin));
  if (p.priceMax != null) sp.set('priceMax', String(p.priceMax));
  if (p.sortBy) sp.set('sortBy', p.sortBy);
  sp.set('page', String(p.page ?? 0));
  sp.set('size', String(p.size ?? 20));
}

async function runSearch(path: string, sp: URLSearchParams, accessToken?: string): Promise<OfferingSearchPageResponse> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await apiFetchWithRetry(`${path}?${sp.toString()}`, { headers });
  if (!res.ok) throw new Error(`Offering search failed: ${res.status}`);
  return res.json();
}

export async function searchOneToOne(params: CommonOfferingSearchParams): Promise<OfferingSearchPageResponse> {
  const sp = new URLSearchParams();
  buildCommon(sp, params);
  return runSearch('/api/v1/marketplace/offerings/one-to-one', sp, params.accessToken);
}

export async function searchGroup(params: GroupOfferingSearchParams): Promise<OfferingSearchPageResponse> {
  const sp = new URLSearchParams();
  buildCommon(sp, params);
  if (params.batchSizeMax != null) sp.set('batchSizeMax', String(params.batchSizeMax));
  if (params.sessionsPerWeek != null) sp.set('sessionsPerWeek', String(params.sessionsPerWeek));
  if (params.sessionDurationMinutes != null) sp.set('sessionDurationMinutes', String(params.sessionDurationMinutes));
  if (params.startsWithinDays != null) sp.set('startsWithinDays', String(params.startsWithinDays));
  return runSearch('/api/v1/marketplace/offerings/group', sp, params.accessToken);
}

export async function searchCrashCourse(params: CrashCourseSearchParams): Promise<OfferingSearchPageResponse> {
  const sp = new URLSearchParams();
  buildCommon(sp, params);
  if (params.sessionDurationMinutes != null) sp.set('sessionDurationMinutes', String(params.sessionDurationMinutes));
  if (params.mockTestsIncluded != null) sp.set('mockTestsIncluded', String(params.mockTestsIncluded));
  if (params.courseStartDate) sp.set('courseStartDate', params.courseStartDate);
  if (params.courseEndDate) sp.set('courseEndDate', params.courseEndDate);
  if (params.startsWithinDays != null) sp.set('startsWithinDays', String(params.startsWithinDays));
  return runSearch('/api/v1/marketplace/offerings/crash-course', sp, params.accessToken);
}

/** Per-offering match-score breakdown (offering-scoped, ADR-097). */
export async function getOfferingMatchScore(
  offeringId: string,
  accessToken: string | null | undefined,
  ctx?: SearchContextSnapshot | null,
): Promise<MatchScoreBreakdown> {
  const params = new URLSearchParams();
  if (ctx?.subject) params.set('subject', ctx.subject);
  if (ctx?.gradeBand) params.set('gradeBand', ctx.gradeBand);
  if (ctx?.curriculum) params.set('curriculum', ctx.curriculum);
  if (ctx?.budgetMax != null) params.set('budgetMax', String(ctx.budgetMax));
  if (ctx?.examId) params.set('examId', ctx.examId);
  if (ctx?.language) params.set('language', ctx.language);
  const qs = params.toString();
  const url = `/api/v1/marketplace/offerings/${encodeURIComponent(offeringId)}/match-score${qs ? '?' + qs : ''}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await apiFetch(url, { headers });
  if (!res.ok) throw new Error(`Offering match score fetch failed: ${res.status}`);
  return res.json();
}

/** Map an offering-type slug used in routes to the API path segment / enum. */
export const OFFERING_TYPE_ROUTE: Record<string, OfferingType> = {
  'one-to-one': 'ONE_TO_ONE',
  'group': 'GROUP',
  'crash-course': 'CRASH_COURSE',
};
